import { useEffect, useRef } from 'react';
import { useIsEditMode } from 'stores/edit-mode-enum';
import { BUILD_ID, isLocalBuild } from 'utils/build-id';

/**
 * Reloads the tab when a newer build has been deployed.
 *
 * Kiosk screens run for weeks without a manual refresh, so a deploy would
 * otherwise never reach them. See docs/stale-detection.md.
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

const RELOADED_FOR_KEY = 'tv-app:reloaded-for-build';

const fetchDeployedBuildId = async (signal: AbortSignal) => {
  const url = `${process.env.PUBLIC_URL}/index.html?build-check=${Date.now()}`;
  const response = await fetch(url, { cache: 'no-store', signal });

  if (!response.ok) {
    return null;
  }

  const html = await response.text();
  const meta = new DOMParser().parseFromString(html, 'text/html').querySelector('meta[name="build-id"]');

  return meta?.getAttribute('content') || null;
};

export const useStaleBuildDetection = () => {
  const [isEditMode] = useIsEditMode();
  // Via ref so toggling edit mode doesn't tear down the interval.
  const isEditModeRef = useRef(isEditMode);
  isEditModeRef.current = isEditMode;

  useEffect(() => {
    if (isLocalBuild) {
      return;
    }

    const controller = new AbortController();

    const check = async () => {
      // Reloading mid-edit would discard unsaved layout changes.
      if (isEditModeRef.current) {
        return;
      }

      try {
        const deployedBuildId = await fetchDeployedBuildId(controller.signal);

        if (!deployedBuildId || deployedBuildId === BUILD_ID) {
          return;
        }

        // Guards against a reload loop if index.html is ever served stale.
        if (window.sessionStorage.getItem(RELOADED_FOR_KEY) === deployedBuildId) {
          return;
        }

        window.sessionStorage.setItem(RELOADED_FOR_KEY, deployedBuildId);
        window.location.reload();
      } catch {
        // Offline, DNS blip, mid-deploy 502 — retry on the next tick.
      }
    };

    const intervalId = window.setInterval(check, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      controller.abort();
    };
  }, []);
};
